import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Locales
import translate from 'locales/i18n';
//Mui components
import Button from '@mui/material/Button';
import Alert from '@mui/lab/Alert';
//PCP components
import Loader from 'components/commons/Loader';
import ResumeImportSheet from 'components/sheets/ResumeImportSheet.jsx';
import DialogList from 'components/dialogs/commons/DialogList';
//Actions
import ItemAction from 'actions/ItemAction.js';
import AccountStore from 'stores/AccountStore';
//Style
import { useStyles } from 'components/sheets/AccountImportSheet/AccountImportSheet.style';
//Utils
import { getItems, getRequiredMatchings, getNoneRequiredMatchings } from 'components/sheets/AccountImportSheet/AccountImport.utils';
import Facade from 'core/Facade';

const AccountImportFinalStep = ({ selectedAccount, keys, itemRules, attributeRules, matchings, importType }) => {

    const classes = useStyles();

    const [showDialogList, setShowDialogList] = useState(false);
    const [dialogListData, setDialogListData] = useState([]);
    const [dialogListTitle, setDialogListTitle] = useState('');
    const [accountUpdateStats, setAccountUpdateStats] = useState(null);
    const [updatedAdditionalData, setUpdatedAdditionalData] = useState({});
    const [isUpdatingAccount, setIsUpdatingAccount] = useState(AccountStore.isUpdatingAccount());
    const [accountImportError, setAccountImportError] = useState(true);

    useEffect(() => {
        AccountStore.addChangeListener(onAccountStoreChanges);
        return () => AccountStore.removeChangeListener(onAccountStoreChanges);
    }, []);

    const onAccountStoreChanges = () => {
        setAccountUpdateStats(AccountStore.getAccountUpdateStats());
        setUpdatedAdditionalData(AccountStore.getUpdatedAdditionalDatas());
        setIsUpdatingAccount(AccountStore.isUpdatingAccount());
    };

    const requiredMatchingsSelected = getRequiredMatchings(matchings).filter(matching => matching[1].header !== '');
    const noneRequiredMatchingsSelected = getNoneRequiredMatchings(matchings).filter(matching => matching[1].header !== '');

    const openDialogList = (data, title) => {
        setShowDialogList(true);
        setDialogListData(data);
        setDialogListTitle(title);
    };

    const handleCloseDialogList = () => {
        setShowDialogList(false);
        setDialogListData([]);
        setDialogListTitle('');
    };

    const launchImport = () => {
        if (importType === 'account') {
            ItemAction.execImportItems(
                keys,
                Object.keys(matchings).filter(
                    m => matchings[m].header !== '' && !keys.includes(m)
                ),
                getItems(matchings, keys, importType, false),
                itemRules,
                attributeRules,
                selectedAccount.id
            );
        } else {
            ItemAction.importAdditionalDatas(getItems(matchings, keys, importType, false), selectedAccount.id).then(e => {
                setAccountImportError(e);
            });
        }
    };

    const renderDialogList = () =>
        <DialogList
            open={showDialogList}
            close={handleCloseDialogList}
            dataArray={dialogListData}
            title={dialogListTitle}
        />;

    const renderUpdateStats = () => {

        if (!accountUpdateStats && Facade.isEmpty(updatedAdditionalData) && accountImportError) return null;

        return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                {!accountImportError ?
                    <Alert severity='error' style={{ width: '50%', marginTop: '10px' }}>
                        Il y a eu une erreur, vérifiez le fichier et réessayez
                    </Alert>
                    :
                    !Facade.isEmpty(updatedAdditionalData) ?
                        updatedAdditionalData.notUpdated.length !== 0 ?
                            <Alert severity='error' style={{ width: '50%', marginTop: '10px' }}>
                                Il y a {updatedAdditionalData.notUpdated.length} articles qui ont pas été mis a jour.
                            </Alert>
                            :
                            <Alert severity='success' style={{ width: '50%', marginTop: '10px' }}>
                                Les {updatedAdditionalData.updated.length} articles ont été mis a jour.
                            </Alert>
                        :
                        <div className={classes.accountUpdateStatsContainer}>
                            <div>
                                <div>
                                    {accountUpdateStats.created} articles ont été créés.
                                </div>
                                <div>
                                    {accountUpdateStats.updated} articles ont été mis à jour.
                                </div>
                                <div>
                                    {accountUpdateStats.deleted} articles ont été supprimés.
                                </div>
                                <div>
                                    {accountUpdateStats.categoriesCreated} catégories ont été créés.
                                </div>
                            </div>
                        </div>
                }
            </div>
        );
    };

    return (
        <div style={{ overflow: 'auto' }}>
            {renderDialogList()}
            <div className={classes.formContent} style={{ margin: '40px' }}>
                {
                    isUpdatingAccount ?
                        <Loader size={50} />
                        :
                        <Button
                            style={{ marginLeft: '8px' }}
                            variant='contained'
                            color='primary'
                            onClick={() => launchImport()}
                            align='center'
                            disabled={isUpdatingAccount}
                        >
                            {translate.i18n('IMPORT')}
                        </Button>
                }
            </div>
            <div className={classes.formContent}>
                <div className={classes.title}> {translate.i18n('MATCHING_SUMARY_ATTRIBUTE_REQUIRED')} </div>
            </div>
            <ResumeImportSheet
                matchings={requiredMatchingsSelected}
                showDialogList={openDialogList}
            />

            <div className={classes.formContent}>
                <div className={classes.title}> {translate.i18n('MATCHING_SUMARY_ATTRIBUTE_OPTIONAL')} </div>
            </div>
            <ResumeImportSheet
                matchings={noneRequiredMatchingsSelected}
                showDialogList={openDialogList}
            />
            <div className={classes.formContent}>
                {renderUpdateStats()}
            </div>
        </div>
    );
};

AccountImportFinalStep.propTypes = {
    attributeRules: PropTypes.array,
    importType: PropTypes.string,
    itemRules: PropTypes.array,
    keys: PropTypes.array,
    matchings: PropTypes.object,
    selectedAccount: PropTypes.object
};

export default AccountImportFinalStep;
